import { motion } from "framer-motion";
import { MapEventType } from "maplibre-gl";
import React from "react";
import { MapRef } from "react-map-gl/maplibre";
import { useCallbackRef } from "../../../hooks/useCallbackRef";
import { PatientTaskBroadcastDetails, PatientTaskInstance } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { translate } from "../../../translate";
import { List } from "../../../utils/List";
import { getAssignedTaskStatus } from "../../../utils/taskUtils";
import { getStatusColor, getTaskStatus } from "../../AvailableVisitsAndTasksList";
import BaseMap from "../../BaseMap";
import useZoomOutToClosestMarker from "../hooks/useZoomOutToClosestVisit";
import { MainScreenActiveCard } from "../main-utils";
import AssignedTaskMapMarker from "./AssignedTaskMapMarker";
import CaregiverMapMarker from "./CaregiverMapMarker";
import DismissableMainLayoutBanner from "./DismissableMainLayoutBanner";
import { DefinedPlaceResult } from "./MainNavbar";
import SearchedPlaceMapMarker from "./SearchedPlaceMapMarker";
import TaskMapMarker from "./TaskMapMarker";
import VisitMapMarker from "./VisitMapMarker";

type Props = {
  caregiver: AppCaregiver;
  visits: List<Visit>;
  visitsOutsideAvailability: List<Visit>;
  tasks: List<PatientTaskBroadcastDetails>;
  assignedTasks: List<PatientTaskInstance>;
  activeCard: MainScreenActiveCard | null;
  mapRef: MapRef | null;
  searchedPlace: DefinedPlaceResult | null;
  setMapRef: React.Dispatch<React.SetStateAction<MapRef | null>>;
  showVisitsOutsideAvailability: boolean;
  onToggleShowVisitsOutsideAvailability: () => void;
  onClickMap: () => void;
  onClickCaregiverMarker: (e: React.MouseEvent<Element>) => void;
  onClickVisitMarker: (visit: Visit, e: React.MouseEvent<Element>) => void;
  onClickTaskMarker: (task: PatientTaskBroadcastDetails, e: React.MouseEvent<Element>) => void;
  onClickAssignedTaskMarker: (task: PatientTaskInstance, e: React.MouseEvent<Element>) => void;
};

function useMapEvent<TMapEventType extends keyof MapEventType>(
  mapRef: MapRef | null,
  event: TMapEventType,
  callback: (ev: MapEventType[TMapEventType]) => void
) {
  const callbackRef = useCallbackRef(callback);

  React.useEffect(() => {
    if (!mapRef) {
      return;
    }

    mapRef.on(event, callbackRef);

    return () => {
      mapRef.off(event, callbackRef);
    };
  }, [callbackRef, event, mapRef]);
}

export function MainLayoutMapView(props: Props) {
  const [mapZoom, setMapZoom] = React.useState<number>(() => props.mapRef?.getZoom() ?? 16);
  const [isBannerOpen, setIsBannerOpen] = React.useState(false);
  const visits = React.useMemo(
    () =>
      props.showVisitsOutsideAvailability
        ? props.visits.concat(props.visitsOutsideAvailability)
        : props.visits,
    [props.showVisitsOutsideAvailability, props.visits, props.visitsOutsideAvailability]
  );

  React.useEffect(() => {
    if (props.visitsOutsideAvailability.length() === 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsBannerOpen(true);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [props.visitsOutsideAvailability]);

  useMapEvent(props.mapRef, "zoom", (map) => {
    setMapZoom(Math.round(map.target.getZoom() * 10) / 10);
  });

  useZoomOutToClosestMarker({
    mapRef: props.mapRef,
    markerLocations: visits
      .map((visit) => visit.publicVisitBroadcastDetails.location.location)
      .concat(
        props.tasks.flatMap(({ patientAddressComponents }) =>
          patientAddressComponents === null
            ? List.empty()
            : List.fromArray([patientAddressComponents.location])
        )
      )
      .concat(
        props.assignedTasks.flatMap(({ taskDetails }) =>
          taskDetails.patientAddressComponent === null
            ? List.empty()
            : List.fromArray([taskDetails.patientAddressComponent.location])
        )
      ),
    caregiverLocation: props.caregiver.addressGeoLocation,
  });

  const handleShowAllVisits = () => {
    props.onToggleShowVisitsOutsideAvailability();
    setIsBannerOpen(false);
  };

  return (
    <motion.div initial={{ opacity: 0, height: "100%" }} animate={{ opacity: 1 }}>
      <BaseMap
        ref={props.setMapRef}
        destination={props.caregiver.addressGeoLocation}
        zoom={16}
        onClick={props.onClickMap}
      >
        <CaregiverMapMarker
          isActive={props.activeCard?.type === "caregiver"}
          caregiver={props.caregiver}
          onClick={props.onClickCaregiverMarker}
        />

        {props.searchedPlace && <SearchedPlaceMapMarker place={props.searchedPlace} />}

        {visits.map((visit) => (
          <VisitMapMarker
            key={`${visit.id}`}
            isActive={props.activeCard?.type === "visit" && props.activeCard.visit.id === visit.id}
            visit={visit}
            expanded={mapZoom > 10.5}
            onClick={(e) => props.onClickVisitMarker(visit, e)}
          />
        ))}

        {props.tasks.map((task) => (
          <TaskMapMarker
            key={`${task.taskInstanceId}`}
            title={task.title}
            addressComponents={task.patientAddressComponents}
            statusColor={getStatusColor(getTaskStatus(task))}
            isActive={
              props.activeCard?.type === "task" &&
              props.activeCard.task.taskInstanceId === task.taskInstanceId
            }
            expanded={mapZoom > 10.5}
            onClick={(e) => props.onClickTaskMarker(task, e)}
          />
        ))}

        {props.assignedTasks.map((task) => (
          <AssignedTaskMapMarker
            key={`${task.id}`}
            status={getAssignedTaskStatus(task)}
            title={task.taskDetails.title}
            addressComponents={task.taskDetails.patientAddressComponent}
            isActive={
              props.activeCard?.type === "assigned-task" && props.activeCard.task.id === task.id
            }
            expanded={mapZoom > 10.5}
            onClick={(e) => props.onClickAssignedTaskMarker(task, e)}
          />
        ))}
      </BaseMap>

      <DismissableMainLayoutBanner
        text={translate(STRINGS.MORE_VISITS_OUTSIDE_YOUR_AVAILABILITY)}
        actionText={translate(STRINGS.VIEW_ALL)}
        isOpen={isBannerOpen}
        onClickAction={handleShowAllVisits}
        onClose={() => setIsBannerOpen(false)}
      />
    </motion.div>
  );
}
